import React from 'react';
import {
  FaGraduationCap,
  FaBullseye,
  FaBriefcase,
  FaFlask,
  FaUsers,
  FaCode,
  FaLaptopCode,
  FaServer,
  FaBrain,
  FaCloud,
  FaLayerGroup,
} from 'react-icons/fa';
import { FadeIn } from './animations/MotionPrimitives';
import SectionCourier from './illustrations/SectionCourier';
import CourierSatchel from './illustrations/CourierSatchel';
import './styles/About.css';

/* Five facts a recruiter looks for first, as a balanced full-width row
   beneath the biography rather than a narrow side panel. */
const GLANCE = [
  {
    label: 'Degree',
    value: 'M.S. Computer Science',
    detail: 'Illinois Institute of Technology',
    tone: 'blue',
    icon: <FaGraduationCap aria-hidden="true" />,
  },
  {
    label: 'Focus',
    value: 'Software Engineering and AI',
    tone: 'sage',
    icon: <FaBullseye aria-hidden="true" />,
  },
  {
    label: 'Current Role',
    value: 'Software Engineering Intern',
    detail: 'International Farming',
    tone: 'gold',
    icon: <FaBriefcase aria-hidden="true" />,
  },
  {
    label: 'Research',
    value: 'Subtype generalization in image classification',
    tone: 'lav',
    icon: <FaFlask aria-hidden="true" />,
  },
  {
    label: 'Leadership',
    value: 'Corporate Chair',
    detail: 'Society of Women Engineers',
    tone: 'blush',
    icon: <FaUsers aria-hidden="true" />,
  },
];

/* Toolkit groups. Each group carries its own icon and pastel accent so the six
   cards are told apart by category, not by six identical walls of pills. */
const TOOLKIT = [
  {
    group: 'Languages',
    tone: 'gold',
    icon: <FaCode aria-hidden="true" />,
    items: ['Python', 'TypeScript', 'JavaScript', 'Java', 'SQL', 'C++'],
  },
  {
    group: 'Frontend',
    tone: 'blue',
    icon: <FaLaptopCode aria-hidden="true" />,
    items: ['React', 'Next.js', 'Angular', 'React Native', 'HTML', 'CSS', 'Responsive UI'],
  },
  {
    group: 'Backend & APIs',
    tone: 'sage',
    icon: <FaServer aria-hidden="true" />,
    items: ['Node.js', 'Express', 'FastAPI', 'REST', 'GraphQL', 'PostgreSQL', 'Prisma'],
  },
  {
    group: 'AI & Data',
    tone: 'lav',
    icon: <FaBrain aria-hidden="true" />,
    items: [
      'Machine Learning',
      'OpenAI APIs',
      'Anthropic API',
      'RAG',
      'LangChain',
      'LangGraph',
      'Pandas',
      'Streamlit',
    ],
  },
  {
    group: 'Cloud, Testing & Delivery',
    tone: 'blush',
    icon: <FaCloud aria-hidden="true" />,
    items: ['AWS', 'Azure', 'Docker', 'Git', 'GitHub', 'Cypress', 'Jest', 'Postman', 'Chrome DevTools'],
  },
  {
    group: 'Platforms',
    tone: 'teal',
    icon: <FaLayerGroup aria-hidden="true" />,
    items: ['Adobe Experience Manager', 'ArcGIS', 'Formstack', 'Adobe Campaign'],
  },
];

function About() {
  return (
    <section className="section about" id="about">
      <div className="section-inner">
        {/* Editorial opening: the chapter marker and name sit in the left
            column, the biography reads down the wider right column. */}
        <div className="about-top">
          <FadeIn className="chapter-head about-head">
            <span className="chapter-mark">01</span>
            <h2 className="chapter-title">About</h2>
            <p className="about-statement">I build software people can trust.</p>
          </FadeIn>

          <FadeIn className="about-bio" delay={0.06}>
            <p className="about-lead">
              I&apos;m a software engineer and Computer Science graduate student focused on
              full-stack products, AI systems and reliable user experiences. My work spans
              production web applications, developer tooling, public-sector platforms,
              geospatial data and machine-learning research.
            </p>
            <p>
              I enjoy untangling complex workflows, tracing problems across the stack and
              turning them into software that is clearer, more dependable and easier to use.
            </p>
          </FadeIn>
        </div>

        {/* At a Glance: full-width, five balanced tiles */}
        <FadeIn className="glance" delay={0.1}>
          <div className="glance-head">
            <h3 className="glance-title">At a Glance</h3>
            <SectionCourier
              className="glance-bird"
              carry="letter"
              width={58}
              from={{ x: -14, y: 10, rotate: -12 }}
              restRotate={4}
              delay={0.12}
            />
          </div>

          <ul className="glance-grid">
            {GLANCE.map((item) => (
              <li className="gtile" key={item.label}>
                <span className={`gtile-icon gtile-icon--${item.tone}`} aria-hidden="true">
                  {item.icon}
                </span>
                <span className="gtile-label">{item.label}</span>
                <span className="gtile-value">{item.value}</span>
                {item.detail && <span className="gtile-detail">{item.detail}</span>}
              </li>
            ))}
          </ul>
        </FadeIn>

        {/* Technical Toolkit: its own chapter panel with a cool wash */}
        <FadeIn className="toolkit" delay={0.08}>
          <div className="chapter-head toolkit-head">
            <div>
              {/* motif only: the name is stated once, in the heading */}
              <span className="chapter-mark chapter-mark--bare" aria-hidden="true" />
              <h3 className="chapter-title toolkit-title">Technical Toolkit</h3>
            </div>
            <div className="toolkit-satchel">
              <CourierSatchel width={78} />
              <SectionCourier
                className="toolkit-bird"
                carry="none"
                width={54}
                from={{ x: 12, y: 14, rotate: 12 }}
                restRotate={-5}
                delay={0.1}
              />
            </div>
          </div>

          <div className="toolkit-grid">
            {TOOLKIT.map((category) => (
              <article className={`tk-card tk-card--${category.tone}`} key={category.group}>
                <div className="tk-card-head">
                  <span className="tk-icon" aria-hidden="true">
                    {category.icon}
                  </span>
                  <h4 className="tk-card-title">{category.group}</h4>
                </div>
                <ul className="tk-tags">
                  {category.items.map((item) => (
                    <li className="tk-tag" key={item}>
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

export default About;
